import { createClient, type SupabaseClient, type User, type Session } from '@supabase/supabase-js'
import type {
  Profile,
  Product,
  Order,
  OrderItem,
  UserProduct,
  Video,
  ComplaintChannel,
  Complaint,
  ComplaintResponse,
  PaymentHistory,
  Contact,
  Coupon,
  News,
  NewsCategory,
  SiteSettings,
  ApiResult,
} from '@/types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase URL ou Anon Key não configurados!')
}

export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
})

// ============================================
// AUTENTICAÇÃO
// ============================================

export const auth = {
  async signUp(
    email: string,
    password: string,
    metadata: Record<string, unknown> = {}
  ): Promise<{ data: { user: User | null; session: Session | null } | null; error: Error | null }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    })
    return { data, error }
  },

  async signIn(
    email: string,
    password: string
  ): Promise<{ data: { user: User | null; session: Session | null } | null; error: Error | null }> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  async signOut(): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  async resetPassword(email: string): Promise<{ data: object | null; error: Error | null }> {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { data, error }
  },

  async updatePassword(newPassword: string): Promise<{ data: { user: User | null } | null; error: Error | null }> {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    })
    return { data, error }
  },

  async getSession(): Promise<{ session: Session | null; error: Error | null }> {
    const { data, error } = await supabase.auth.getSession()
    return { session: data?.session ?? null, error }
  },

  async getUser(): Promise<{ user: User | null; error: Error | null }> {
    const { data, error } = await supabase.auth.getUser()
    return { user: data?.user ?? null, error }
  },

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback)
  },
}

// ============================================
// PERFIS
// ============================================

export const profiles = {
  async get(userId: string): Promise<ApiResult<Profile>> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error: error?.message ?? null }
  },

  async update(userId: string, updates: Partial<Profile>): Promise<ApiResult<Profile>> {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// PRODUTOS
// ============================================

interface ProductQueryOptions {
  featured?: boolean
  type?: string
  limit?: number
}

export const products = {
  async getAll(options: ProductQueryOptions = {}): Promise<ApiResult<Product[]>> {
    let query = supabase
      .from('products')
      .select('*')
      .eq('is_active', true)

    if (options.featured) {
      query = query.eq('is_featured', true)
    }

    if (options.type) {
      query = query.eq('type', options.type)
    }

    if (options.limit) {
      query = query.limit(options.limit)
    }

    const { data, error } = await query.order('created_at', { ascending: false })
    return { data, error: error?.message ?? null }
  },

  async getBySlug(slug: string): Promise<ApiResult<Product>> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single()
    return { data, error: error?.message ?? null }
  },

  async getById(id: string): Promise<ApiResult<Product>> {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// PEDIDOS
// ============================================

export const orders = {
  async create(orderData: Partial<Order>): Promise<ApiResult<Order>> {
    const { data, error } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },

  async addItems(items: Partial<OrderItem>[]): Promise<ApiResult<OrderItem[]>> {
    const { data, error } = await supabase
      .from('order_items')
      .insert(items)
      .select()
    return { data, error: error?.message ?? null }
  },

  async getUserOrders(userId: string): Promise<ApiResult<Order[]>> {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          products (name, slug, thumbnail_url)
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error: error?.message ?? null }
  },

  async getById(orderId: string): Promise<ApiResult<Order>> {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        order_items (
          *,
          products (*)
        )
      `)
      .eq('id', orderId)
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// PRODUTOS DO USUÁRIO
// ============================================

export const userProducts = {
  async getUserProducts(userId: string): Promise<ApiResult<UserProduct[]>> {
    const { data, error } = await supabase
      .from('user_products')
      .select(`
        *,
        products (*)
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
    return { data, error: error?.message ?? null }
  },

  async hasAccess(userId: string, productId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_products')
      .select('id, expires_at')
      .eq('user_id', userId)
      .eq('product_id', productId)
      .eq('is_active', true)
      .single()

    if (error || !data) return false

    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return false
    }

    return true
  },

  async create(productData: {
    user_id: string
    product_id: string
    order_id: string
    expires_at?: string | null
  }): Promise<ApiResult<UserProduct>> {
    const { data, error } = await supabase
      .from('user_products')
      .insert({
        user_id: productData.user_id,
        product_id: productData.product_id,
        order_id: productData.order_id,
        is_active: true,
        expires_at: productData.expires_at || null,
        access_granted_at: new Date().toISOString(),
      })
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// VÍDEOS
// ============================================

export const videos = {
  async getByProduct(productId: string): Promise<ApiResult<Video[]>> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('product_id', productId)
      .order('order_index', { ascending: true })
    return { data, error: error?.message ?? null }
  },

  async getPublicVideos(): Promise<ApiResult<Video[]>> {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// CANAL DE DENÚNCIAS
// ============================================

export const complaintChannels = {
  async getByUser(userId: string): Promise<ApiResult<ComplaintChannel>> {
    const { data, error } = await supabase
      .from('complaint_channels')
      .select('*')
      .eq('user_id', userId)
      .single()
    return { data, error: error?.message ?? null }
  },

  async getBySlug(slug: string): Promise<ApiResult<ComplaintChannel>> {
    const { data, error } = await supabase
      .from('complaint_channels')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single()
    return { data, error: error?.message ?? null }
  },

  async create(channelData: {
    user_id: string
    company_name: string
    slug: string
    contact_email?: string
  }): Promise<ApiResult<ComplaintChannel>> {
    const { data, error } = await supabase
      .from('complaint_channels')
      .insert({
        user_id: channelData.user_id,
        company_name: channelData.company_name,
        slug: channelData.slug,
        contact_email: channelData.contact_email || null,
        welcome_message: 'Este é um canal seguro e confidencial para você reportar situações que precisam de atenção. Sua identidade será protegida.',
        primary_color: '#1e40af',
        secondary_color: '#3b82f6',
        is_active: true,
        settings: {
          allow_anonymous: true,
          require_category: true,
          notify_on_new: true,
          auto_response: true,
        },
      })
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },

  async update(channelId: string, updates: Partial<ComplaintChannel>): Promise<ApiResult<ComplaintChannel>> {
    const { data, error } = await supabase
      .from('complaint_channels')
      .update(updates)
      .eq('id', channelId)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// DENÚNCIAS
// ============================================

interface ComplaintQueryOptions {
  status?: string
}

export const complaints = {
  async create(complaintData: Partial<Complaint>): Promise<ApiResult<Complaint>> {
    const { data, error } = await supabase
      .from('complaints')
      .insert(complaintData)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },

  async getByChannel(channelId: string, options: ComplaintQueryOptions = {}): Promise<ApiResult<Complaint[]>> {
    let query = supabase
      .from('complaints')
      .select('*')
      .eq('channel_id', channelId)

    if (options.status) {
      query = query.eq('status', options.status)
    }

    const { data, error } = await query.order('created_at', { ascending: false })
    return { data, error: error?.message ?? null }
  },

  async getById(complaintId: string): Promise<ApiResult<Complaint>> {
    const { data, error } = await supabase
      .from('complaints')
      .select(`
        *,
        complaint_responses (*)
      `)
      .eq('id', complaintId)
      .single()
    return { data, error: error?.message ?? null }
  },

  async getByProtocol(protocolNumber: string): Promise<ApiResult<Complaint>> {
    const { data, error } = await supabase
      .from('complaints')
      .select(`
        *,
        complaint_responses (*)
      `)
      .eq('protocol_number', protocolNumber.trim().toUpperCase())
      .single()
    return { data, error: error?.message ?? null }
  },

  async updateStatus(
    complaintId: string,
    status: string,
    notes: string | null = null
  ): Promise<ApiResult<Complaint>> {
    const updates: Record<string, unknown> = { status }
    if (notes) updates.internal_notes = notes
    if (status === 'resolved') updates.resolved_at = new Date().toISOString()

    const { data, error } = await supabase
      .from('complaints')
      .update(updates)
      .eq('id', complaintId)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },

  async addResponse(responseData: Partial<ComplaintResponse>): Promise<ApiResult<ComplaintResponse>> {
    const { data, error } = await supabase
      .from('complaint_responses')
      .insert(responseData)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// CONTATOS
// ============================================

export const contacts = {
  async create(contactData: Partial<Contact>): Promise<ApiResult<Contact>> {
    const { data, error } = await supabase
      .from('contacts')
      .insert(contactData)
      .select()
      .single()
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// CUPONS
// ============================================

interface CouponValidationResult {
  valid: boolean
  coupon?: Coupon
  error?: string
}

export const coupons = {
  async validate(code: string): Promise<CouponValidationResult> {
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('code', code.toUpperCase())
      .eq('is_active', true)
      .single()

    if (error || !data) {
      return { valid: false, error: 'Cupom inválido' }
    }

    const now = new Date()
    if (data.valid_from && new Date(data.valid_from) > now) {
      return { valid: false, error: 'Cupom ainda não está válido' }
    }
    if (data.valid_until && new Date(data.valid_until) < now) {
      return { valid: false, error: 'Cupom expirado' }
    }

    if (data.max_uses && data.uses_count >= data.max_uses) {
      return { valid: false, error: 'Cupom esgotado' }
    }

    return { valid: true, coupon: data }
  },
}

// ============================================
// HISTÓRICO DE PAGAMENTOS
// ============================================

export const paymentHistory = {
  async getUserHistory(userId: string): Promise<ApiResult<PaymentHistory[]>> {
    const { data, error } = await supabase
      .from('payment_history')
      .select(`
        *,
        orders (order_number, total_amount)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// CONFIGURAÇÕES DO SITE
// ============================================

export const settings = {
  async get<T>(key: string): Promise<ApiResult<T>> {
    const { data, error } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', key)
      .single()
    return { data: data?.value ?? null, error: error?.message ?? null }
  },

  async getAll(): Promise<ApiResult<SiteSettings>> {
    const { data, error } = await supabase
      .from('site_settings')
      .select('*')

    const settingsObj: Record<string, unknown> = {}
    data?.forEach((item: { key: string; value: unknown }) => {
      settingsObj[item.key] = item.value
    })

    return { data: settingsObj as unknown as SiteSettings, error: error?.message ?? null }
  },
}

// ============================================
// ANEXOS DE DENÚNCIAS
// ============================================

export const complaintAttachments = {
  async upload(channelId: string, file: File): Promise<ApiResult<string>> {
    const fileExt = file.name.split('.').pop()
    const fileName = `${channelId}/${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`

    const { error } = await supabase.storage
      .from('complaint-attachments')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) return { data: null, error: error.message }

    const { data: urlData } = supabase.storage
      .from('complaint-attachments')
      .getPublicUrl(fileName)

    return { data: urlData.publicUrl, error: null }
  },
}

// ============================================
// EDGE FUNCTIONS
// ============================================

export const functions = {
  async invoke<T>(functionName: string, payload: Record<string, unknown> = {}): Promise<ApiResult<T>> {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: payload,
    })
    return { data, error: error?.message ?? null }
  },
}

// ============================================
// NOTÍCIAS / CMS
// ============================================

interface NewsQueryOptions {
  featured?: boolean
  categorySlug?: string
  limit?: number
}

export const news = {
  async getAll(options: NewsQueryOptions = {}): Promise<ApiResult<News[]>> {
    let query = supabase
      .from('news')
      .select('*, category:news_categories(*)')
      .eq('is_active', true)

    if (options.featured) {
      query = query.eq('is_featured', true)
    }

    if (options.categorySlug) {
      const { data: categoryData } = await supabase
        .from('news_categories')
        .select('id')
        .eq('slug', options.categorySlug)
        .single()
      if (categoryData?.id) {
        query = query.eq('category_id', categoryData.id)
      }
    }

    if (options.limit) {
      query = query.limit(options.limit)
    }

    const { data, error } = await query.order('published_at', { ascending: false, nullsFirst: false })
    return { data, error: error?.message ?? null }
  },

  async getBySlug(slug: string): Promise<ApiResult<News>> {
    const { data, error } = await supabase
      .from('news')
      .select('*, category:news_categories(*)')
      .eq('slug', slug)
      .eq('is_active', true)
      .single()
    return { data, error: error?.message ?? null }
  },
}

export const newsCategories = {
  async getAll(): Promise<ApiResult<NewsCategory[]>> {
    const { data, error } = await supabase
      .from('news_categories')
      .select('*')
      .eq('is_active', true)
      .order('order_index', { ascending: true })
    return { data, error: error?.message ?? null }
  },
}

export const newsAttachments = {
  async uploadCover(file: File): Promise<ApiResult<string>> {
    const fileExt = file.name.split('.').pop()
    const fileName = `covers/${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`

    const { error } = await supabase.storage
      .from('news')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) return { data: null, error: error.message }

    const { data: urlData } = supabase.storage
      .from('news')
      .getPublicUrl(fileName)

    return { data: urlData.publicUrl, error: null }
  },

  async deleteCover(publicUrl: string): Promise<ApiResult<null>> {
    // Extract path after `/news/` in the public URL
    const marker = '/storage/v1/object/public/news/'
    const idx = publicUrl.indexOf(marker)
    if (idx === -1) {
      return { data: null, error: 'URL inválida' }
    }
    const path = publicUrl.substring(idx + marker.length)
    const { error } = await supabase.storage.from('news').remove([path])
    return { data: null, error: error?.message ?? null }
  },
}

export default supabase
